import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useTour, tourStyles } from '@/hooks/useTour'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { JourneyNavPublisher, hiddenEmployeeSections } from '@/lib/journeySections'
import { VideoHelpButton } from '@/components/guidance/VideoHelpButton'
import type { GuidanceSurface } from '@/lib/guidanceSurfaces'
import { getKpaDescription } from '@/utils/performance/kpaDescriptions'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Search,
  Filter,
  Users,
  Sparkles,
  Plus,
  UserCheck,
  Target,
  BookOpen,
  Calendar,
  Brain,
  TrendingUp,
  CheckSquare,
  Clock,
  Lightbulb,
  MessageSquare,
  User,
  Award,
  LayoutGrid,
  GanttChart,
  AlertTriangle,
  Layers,
  Check,
  ArrowLeft,
  Settings,
  Play,
  HelpCircle,
  Goal,
  Spotlight,
  Fingerprint,
  NotebookPen
} from 'lucide-react'
import Logo from '@/components/ui/Logo'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import KPIWorkspace from '@/components/performance/KPIWorkspace'
import EmployeeKPAView from '@/components/performance/EmployeeKPAView'
import KPIGanttChart from '@/components/gantt/KPIGanttChart'
import { EvidenceEntry, AIInsight, CollaborationSession } from '@/types/objectives'
import { EnhancedEvidenceEntry, JITCheckIn, KPAData } from '@/types/evidence'
import { performanceService } from '@/services/api/performanceService'
import { sessionManager } from '@/services/sessionManager'
import type { RoleKPI, RoleType, ReviewMilestone, EmployeePerformancePlan } from '@/services/api/performanceService'
import { calculateProgress } from '@/utils/kpiProgress'
import type { MeasurementDirection, MeasurementMode } from '@/types/kpi'
import { integrationService, type HistoricalPerformanceResponse } from '@/services/api/integrationService'
import { onboardingService, type OnboardingStatus } from '@/services/api/onboardingService'
import { useAuth } from '@/providers/AuthProvider'
import toast from 'react-hot-toast'

// Import new Employee Journey components
// Note: ManagerCollaborationWorkflow now merged into ReviewsTab
import ObjectiveSettingDialog from '@/components/performance/ObjectiveSettingDialog'
import ImprovedObjectiveWizard from '@/components/performance/objective-setting/ImprovedObjectiveWizard'
// Note: StakeholderFeedbackSystem moved to FeedbackTab
import AnnualProfileManager from '@/components/profile/AnnualProfileManager'
// Note: EmployeeIDPView now used via DevelopmentTab
// Note: ConductReviewWorkspace now used via ReviewsTab
// Note: EmployeeReviewManager now used via ReviewsTab
import ObjectiveConfigurationDialog from '@/components/performance/manager/ObjectiveConfigurationDialog'
import { reviewService } from '@/services/api/reviewService'
// Note: feedbackService moved to FeedbackTab
import { profileService } from '@/services/api/profileService'
// Note: FeedbackRequest, FeedbackStatistics, FeedbackStakeholder, FeedbackRequestDialog moved to FeedbackTab

// Import refactored hooks
import { useEmployeePerformancePlan } from '@/hooks/employee/useEmployeePerformancePlan'
import { useEvidence } from '@/hooks/performance/useEvidence'

// Import refactored tab components
import { ObjectivesTab } from '@/components/employee-journey/tabs/ObjectivesTab'
import { ReviewsTab } from '@/components/employee-journey/tabs/ReviewsTab'
import { FeedbackTab } from '@/components/employee-journey/tabs/FeedbackTab'
import { DevelopmentTab } from '@/components/employee-journey/tabs/DevelopmentTab'
import { PortfolioTab } from '@/components/employee-journey/tabs/PortfolioTab'
import { buildCarriedMidYearEvidence } from '@/lib/evidenceReleaseFilter'
import { loadProfileParts, profileProblem } from '@/lib/profileLoad'
import { getCurrentReviewPeriod } from '@/lib/reviewPeriod'
import { useReviewCycleConfig } from '@/hooks/useReviewCycleConfig'
import { StaffOrderBanner } from '@/pages/staff-order/StaffOrderBanner'

// Helper function to extract numeric ID from employee ID
// For test users: "TEST001" -> 1, "TEST002" -> 2
// For real users: Can be enhanced to use a proper mapping
const extractNumericId = (employeeId: string): number => {
  // Try to extract numeric portion
  const match = employeeId.match(/\d+/)
  if (match) {
    return parseInt(match[0], 10)
  }
  
  // Fallback: create a simple hash
  let hash = 0
  for (let i = 0; i < employeeId.length; i++) {
    const char = employeeId.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash)
}

// Shared light-ghost-on-dark control style used by the header buttons on both
// Journey pages (Employee + Manager). Kept identical in both files so the two
// headers stay visually in sync. min-h-[44px] on mobile guarantees a >=44px
// tap target; relaxes to the compact sm size on desktop.
const HEADER_GHOST_BTN =
  'bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm font-secondary font-medium shadow-lg min-h-[44px] sm:min-h-0 px-3'

// Transform backend session format to frontend format
export const transformJourneySession = (session: any) => ({
  id: session.id,
  employeeId: session.employee_id,
  employeeName: session.employee_name,
  employeeRole: session.employee_role,
  managerId: session.manager_id,
  managerName: session.manager_name,
  sessionType: session.session_type,
  status: session.status === 'pending_scheduling' ? 'pending-scheduling' :
          session.status === 'in_progress' ? 'in-progress' : session.status,
  roleProfileConfirmed: session.role_profile_confirmed || false,
  strategyCascadeReviewed: session.strategy_cascade_reviewed || false,
  objectivesDiscussed: session.objectives_discussed || false,
  objectivesFinalized: session.objectives_finalized || false,
  meetingDetails: session.scheduled_date ? {
    // The date on the same clock as the time below. The stored value is UTC,
    // whose date is still the previous day until 02:00 in South Africa.
    date: format(new Date(session.scheduled_date), 'yyyy-MM-dd'),
    time: (() => {
      // Extract time in HH:mm format from ISO string
      const dateObj = new Date(session.scheduled_date)
      const hours = dateObj.getHours().toString().padStart(2, '0')
      const minutes = dateObj.getMinutes().toString().padStart(2, '0')
      return `${hours}:${minutes}`
    })(),
    location: session.meeting_location || '',
    meetingType: session.meeting_type === 'face_to_face' ? 'face-to-face' :
                session.meeting_type || 'virtual',
    meetingLink: session.meeting_link,
    duration: session.duration_minutes || 60,
    agenda: session.meeting_agenda || '',
    scheduledDateISO: session.scheduled_date  // Store original ISO string for reschedule dialog
  } : undefined,
  collaborationNotes: session.collaboration_notes,
  completedAt: session.completed_at,
  createdAt: session.created_at,
  scheduledBy: session.scheduled_by || 'system'
})

export default function EmployeeJourneyPage() {
  // Get authenticated user
  const { user } = useAuth()
  
  // Interactive tour management
  const { isActive: isTourActive, hasSeenTour, startTour, autoStartTour } = useTour('employee-journey')
  
  // URL params for state persistence
  const [searchParams, setSearchParams] = useSearchParams()
  
  // State management for employee journey - preserve tab across operations
  const [activeTab, setActiveTab] = useState<'objectives' | 'portfolio' | 'planning' | 'templates' | 'feedback' | 'development'>(
    (searchParams.get('tab') as any) || 'objectives'
  )
  // The sidebar navigates by URL; the tab follows it.
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab && tab !== activeTab && ['objectives', 'portfolio', 'planning', 'templates', 'feedback', 'development'].includes(tab)) {
      setActiveTab(tab as any)
    }
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps
  // In-context guidance video bound to the active tab (undefined = no surface /
  // no video → <VideoHelpButton> renders nothing). One mount governs every tab.
  const guidanceSurface: GuidanceSurface | undefined = ({
    objectives: 'tm-objectives',
    portfolio: 'tm-portfolio',
    planning: 'tm-checkins',
    feedback: 'tm-feedback',
    development: 'tm-growth',
  } as Record<string, GuidanceSurface>)[activeTab]
  const [objectivesViewMode, setObjectivesViewMode] = useState<'kpa' | 'timeline'>('kpa')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [objectiveType, setObjectiveType] = useState<'all' | 'strategic' | 'operational' | 'development' | 'innovation'>('all')
  
  // Effective user ID: prefer employeeId from F&O, fallback to database user.id for admin users
  const effectiveUserId = useMemo(() => user?.employeeId || user?.id?.toString() || null, [user?.employeeId, user?.id])
  
  // Employee context from F&O integration
  // TODO: Role switching functionality - currently using employee's actual role from user profile
  // const [selectedRoleType] = useState<RoleType>('lecturer')
  const [employeeContext, setEmployeeContext] = useState({
    jobGroup: 'Loading...',
    reportingManager: 'Loading...',
    reportingManagerId: undefined as string | undefined,
    campus: 'Loading...',
    department: 'Loading...',
    performanceCycle: `${new Date().getFullYear()}`
  })
  // Manager display name now comes from user.managerDisplayName (no separate state needed)
  
  // Onboarding state
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  const [checkingOnboarding, setCheckingOnboarding] = useState(true)
  
  // Employee-specific performance plan state
  const [performancePlan, setPerformancePlan] = useState<EmployeePerformancePlan | null>(null)
  const [hasObjectives, setHasObjectives] = useState<boolean>(false)
  const [roleKPIs, setRoleKPIs] = useState<RoleKPI[]>([])
  const [reviewMilestones, setReviewMilestones] = useState<ReviewMilestone[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Note: Review cycle data state moved to ReviewsTab component
  
  // KPI Snapshot state - for viewing historical performance from completed reviews
  const [kpiSnapshots, setKpiSnapshots] = useState<Map<string, Map<string, number>>>(new Map())
  const currentMonth = new Date().getMonth() + 1
  const currentYear = new Date().getFullYear()
  const reviewCycleCfg = useReviewCycleConfig()

  // Debug: Log the default review period calculation
  const defaultReviewPeriod = getCurrentReviewPeriod(new Date(), reviewCycleCfg)
  if (import.meta.env.DEV) console.log('📅 Review Period Default:', {
    currentMonth,
    currentYear,
    defaultPeriod: defaultReviewPeriod,
    boundary: `${reviewCycleCfg.h2_start_month}/${reviewCycleCfg.h2_start_day}`
  })
  
  const [selectedReviewPeriod, setSelectedReviewPeriod] = useState<'MIDYEAR' | 'YEAREND'>(defaultReviewPeriod)
  // The cycle-boundary config loads async — useReviewCycleConfig returns the
  // 1-Aug default until then, so the useState seed above can be computed from the
  // fallback and would otherwise never update once the real config arrives. Re-sync
  // the default period when the resolved boundary changes, unless the user has
  // already picked a period manually via the selector.
  const userPickedPeriodRef = useRef(false)
  useEffect(() => {
    if (!userPickedPeriodRef.current) {
      setSelectedReviewPeriod(defaultReviewPeriod)
    }
  }, [defaultReviewPeriod])
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i) // 2 years back, current, 2 years forward
  
  // System launch year - years before this are "legacy" (F&O only)
  const SYSTEM_LAUNCH_YEAR = 2026
  const isLegacyYear = selectedYear < SYSTEM_LAUNCH_YEAR
  
  // Historical performance data from F&O (for legacy years)
  const [historicalData, setHistoricalData] = useState<HistoricalPerformanceResponse | null>(null)
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false)
  
  // Reset to objectives tab when switching to legacy year (other tabs don't have F&O data)
  useEffect(() => {
    if (isLegacyYear && activeTab !== 'objectives') {
      setActiveTab('objectives')
    }
  }, [isLegacyYear, activeTab])

  // Outside the review cycle (an ICA, casual staff, a learner, or excluded by HR)
  // the journey offers Portfolio, Feedback and Growth only (hiddenEmployeeSections).
  // Past years' F&O history stays readable.
  const hiddenSections = useMemo(() => hiddenEmployeeSections(isLegacyYear, user?.inReviewCycle),
                                 [isLegacyYear, user?.inReviewCycle])
  const outsideReviewCycle = !isLegacyYear && hiddenSections.includes('objectives')
  useEffect(() => {
    if (outsideReviewCycle && hiddenSections.includes(activeTab)) {
      setActiveTab('portfolio')
      setSearchParams({ tab: 'portfolio' }, { replace: true })  // the sidebar follows the URL
    }
  }, [outsideReviewCycle, hiddenSections, activeTab, setSearchParams])
  
  // Add ref to track if performance plan is already loading
  const isLoadingPerformancePlan = useRef(false)
  
  // AI assistance state
  const [isAIGenerating, setIsAIGenerating] = useState(false)
  const [showAISuggestions, setShowAISuggestions] = useState(false)
  const [aiSuggestions] = useState([
    {
      id: '1',
      type: 'strategic' as const,
      title: 'Enhance Research Output Quality',
      description: 'Increase peer-reviewed publications with focus on AI/ML applications in education',
      smartScore: 85,
      alignedPillar: 'Impact',
      suggestedMetrics: ['Publications per year', 'Citation index', 'Research grant success rate']
    },
    {
      id: '2', 
      type: 'operational' as const,
      title: 'Improve Student Engagement Rates',
      description: 'Implement innovative teaching methods to increase class attendance and participation',
      smartScore: 78,
      alignedPillar: 'Quality',
      suggestedMetrics: ['Class attendance %', 'Student evaluation scores', 'Assignment submission rates']
    }
  ])
  
  // Evidence and collaboration state
  const [selectedObjective, setSelectedObjective] = useState<RoleKPI | null>(null)
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false)
  const [evidence] = useState<EvidenceEntry[]>([])
  const [aiInsights] = useState<AIInsight[]>([])
  const [isAILoading, setIsAILoading] = useState(false)
  
  // KPA Workspace state (embedded view, always shown)
  const [enhancedEvidence, setEnhancedEvidence] = useState<EnhancedEvidenceEntry[]>([])
  const [isLoadingEvidence, setIsLoadingEvidence] = useState(false)
  // A2 — per-entry release model. We carry the user's pending / scheduled
  // review sessions so the Portfolio tab can render "Release for Mid-Year"
  // / "Release for Year-End" buttons per entry. Repopulated whenever the
  // user, year, or evidence list changes (release/withdraw updates session
  // state indirectly through evidence; cheaper to refresh on user-driven
  // events than to subscribe to every individual entry's release status).
  // Note: My Personalised Growth data moved to main page Development tab (cross-KPA planning)
  

  // Note: Manager ratings state moved to ReviewsTab component

  // Employee Journey state additions
  const [userRole] = useState<'employee' | 'manager'>('employee') // TODO: Get from auth context - Set to 'manager' for testing
  const [collaborativeSessions, setCollaborativeSessions] = useState<any[]>([])
  const [loadingCollaborativeSessions, setLoadingCollaborativeSessions] = useState(false)
  
  // Derived: Check if employee has at least one completed collaboration session
  const hasCompletedCollaboration = useMemo(() => {
    return collaborativeSessions.some(session => session.status === 'completed')
  }, [collaborativeSessions])
  // Note: Feedback state moved to FeedbackTab component (self-contained)
  const [showProfileManager, setShowProfileManager] = useState(false)
  const [showObjectiveSourceDialog, setShowObjectiveSourceDialog] = useState(false)
  const [showObjectiveWizard, setShowObjectiveWizard] = useState(false)
  const [employeeProfileData, setEmployeeProfileData] = useState<any>(null)
  const [loadingProfileData, setLoadingProfileData] = useState(false)
  const [internalCareerHistory, setInternalCareerHistory] = useState<any>(null)
  // Note: Review preparation state moved to ReviewsTab component

  // Available roles - hardcoded for now, could be fetched from API (used in templates tab)
  const availableRoles = useMemo(() => [
    { value: 'lecturer' as RoleType, label: 'Lecturer', description: 'Academic staff member', functionalArea: 'Academic Affairs' },
    { value: 'head-of-department' as RoleType, label: 'Head of Department', description: 'Department leadership', functionalArea: 'Academic Affairs' },
    { value: 'dean' as RoleType, label: 'Dean', description: 'Faculty leadership', functionalArea: 'Academic Affairs' },
    { value: 'finance-manager' as RoleType, label: 'Finance Manager', description: 'Financial oversight', functionalArea: 'Finance' },
    { value: 'student-affairs' as RoleType, label: 'Student Affairs Manager', description: 'Student services', functionalArea: 'Student Affairs' },
    { value: 'sales-hec' as RoleType, label: 'Sales HEC', description: 'Higher Education Consultant responsible for student recruitment and enrolment', functionalArea: 'Sales Department' },
  ], [])
  
  // Ensure unused variables are acknowledged for future use
  void availableRoles // Will be used in templates and planning tabs

  // Refetch function that can be called after updates
  const refetchPerformancePlan = useCallback(async () => {
    if (!effectiveUserId) return
    
    try {
      // The year on screen: after adding next year's objectives the page shows next year's
      // plan, not this year's (HR UAT list #27)
      const plan = await performanceService.getEmployeePerformancePlan(
        effectiveUserId, 
        selectedYear, 
        false
      )
      
      setPerformancePlan(plan)
      
      if (plan.has_objectives && plan.objectives.length > 0) {
        const allKPIs: RoleKPI[] = plan.objectives.flatMap(obj => 
          (obj.kpis || []).map(kpi => ({
            id: kpi.id,
            name: kpi.name,
            description: kpi.description || '',
            target_value: kpi.target_value,
            current_value: kpi.current_value || 0,
            unit: kpi.unit,
            frequency: kpi.frequency,
            status: kpi.status as 'on-track' | 'at-risk' | 'off-track',
            role_type: obj.role_type,
            functional_area: obj.functional_area,
            campus: employeeContext.campus,
            owner_id: user!.employeeId || null,
            cascade_to_objective_id: String(obj.id),
            cascade_to_kpa_id: obj.kpa ? String(obj.kpa.id) : null,
            cascade_to_pillar_id: obj.pillar ? String(obj.pillar.id) : null,
            kpa_id: obj.kpa?.id || null,
            pillar_id: obj.pillar?.id || null,
            strategic_objective_id: obj.strategic_objective?.id || null,
            kpa_name: obj.kpa?.name || null,
            pillar_name: obj.pillar?.name || null,
            strategic_objective_name: obj.strategic_objective?.name || null,
            individual_objective_id: obj.id,
            primary_initiative_id: null,
            individual_objective: {
              id: obj.id,
              title: obj.title,
              description: obj.description,
              role_type: obj.role_type,
              owner: user!.displayName || '',
              functional_area: obj.functional_area,
              status: obj.status,
              progress: obj.progress,
              start_date: obj.start_date,
              end_date: obj.end_date,
              strategic_objective_id: obj.strategic_objective?.id || 0,
              kpa_id: obj.kpa?.id || 0,
              pillar_id: obj.pillar?.id || 0,
              kpa: obj.kpa ? {
                id: obj.kpa.id,
                name: obj.kpa.name,
                display_name: obj.kpa.display_name,
                weight_percentage: obj.kpa.weight_percentage,
                sort_order: (obj.kpa as any).sort_order
              } : undefined
            },
            primary_initiative: undefined,
            category: kpi.category,
            owner: user!.displayName,
            priority: kpi.priority || 'medium',
            progress_percentage: kpi.yearend_progress,
            midyear_progress: kpi.midyear_progress,
            yearend_progress: kpi.yearend_progress,
            midyear_value: (kpi as any).midyear_value,  // Period-specific measurement value
            yearend_value: (kpi as any).yearend_value,  // Period-specific measurement value
            trend: kpi.trend || 'stable',
            start_date: kpi.start_date,
            end_date: kpi.end_date,
            is_active: true,
            milestones: kpi.milestones || [],
            measurement_history: (kpi as any).measurement_history,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }))
        )
        setRoleKPIs(allKPIs)
      }
    } catch (error) {
      console.error('Failed to refetch performance plan:', error)
    }
  }, [effectiveUserId, user, selectedYear])

  // Update employee context when user loads
  useEffect(() => {
    if (user) {
      setEmployeeContext(prev => ({
        ...prev,
        jobGroup: user.jobTitle || 'Unknown Job Title',
        campus: user.campus || 'Unknown Campus',
        department: user.functionalArea || user.department || 'Not defined'
      }))
    }
  }, [user])

  // Check onboarding status
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) return
      
      try {
        setCheckingOnboarding(true)
        const status = await onboardingService.getStatus()
        setOnboardingStatus(status)
        
        // Check if user needs to complete onboarding
        const needsCompletion = status.current_stage !== 'completed'
        setNeedsOnboarding(needsCompletion)
        
        if (import.meta.env.DEV) console.log('Onboarding status:', {
          stage: status.current_stage,
          needsCompletion,
          hasObjectives: status.has_objectives
        })
      } catch (error) {
        console.error('Failed to check onboarding status:', error)
        // If status check fails, don't block the user
        setNeedsOnboarding(false)
      } finally {
        setCheckingOnboarding(false)
      }
    }
    
    checkOnboardingStatus()
  }, [user])

  // Load manager details
  useEffect(() => {
    // Use manager display name from user context (already loaded from identity-service)
    const managerName = user?.managerDisplayName || 'None assigned'
    // Use managerEmployeeId (F&O personnel number) for API calls, not managerId (database ID)
    const managerEmployeeId = user?.managerEmployeeId
    setEmployeeContext(prev => ({
      ...prev,
      reportingManager: managerName,
      reportingManagerId: managerEmployeeId
    }))
    
    if (user?.managerDisplayName) {
      if (import.meta.env.DEV) console.log('Manager from user context:', user.managerDisplayName)
    }
  }, [user?.managerDisplayName, user?.managerEmployeeId])

  // Load collaborative sessions for the employee - extracted as reusable function
  const refetchCollaborativeSessions = useCallback(async () => {
    if (!user?.employeeId || sessionManager.isExpired()) return
    
    setLoadingCollaborativeSessions(true)
    try {
      const sessions = await performanceService.getEmployeeSessions(
        user.employeeId,
        { performance_year: selectedYear }
      )
      
      const transformedSessions = (sessions || []).map(transformJourneySession)

      // Filter to deduplicate sessions:
      // - Keep all completed sessions (historical)
      // - For active sessions (pending-scheduling, scheduled, in-progress), only keep the most recent one
      const completedSessions = transformedSessions.filter((s: any) => s.status === 'completed')
      const activeSessions = transformedSessions.filter((s: any) => 
        s.status !== 'completed' && s.status !== 'cancelled'
      )
      
      // Sort active sessions by created date (newest first) and take only the first one
      activeSessions.sort((a: any, b: any) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      const latestActiveSession = activeSessions.length > 0 ? [activeSessions[0]] : []
      
      // Combine: latest active session + all completed sessions
      const deduplicatedSessions = [...latestActiveSession, ...completedSessions]
      
      setCollaborativeSessions(deduplicatedSessions)
      if (import.meta.env.DEV) console.log(`📋 Loaded ${deduplicatedSessions.length} sessions (${latestActiveSession.length} active, ${completedSessions.length} completed) - filtered from ${transformedSessions.length} total for ${user.employeeId}`)
    } catch (error) {
      console.error('Failed to load collaborative sessions:', error)
      // Set empty array on error so UI doesn't break
      setCollaborativeSessions([])
    } finally {
      setLoadingCollaborativeSessions(false)
    }
  }, [user?.employeeId, selectedYear])

  // Load collaborative sessions on mount and when dependencies change
  useEffect(() => {
    refetchCollaborativeSessions()
  }, [refetchCollaborativeSessions])

  // Load employee performance plan on mount
  useEffect(() => {
    const loadEmployeePerformancePlan = async () => {
      if (!effectiveUserId || sessionManager.isExpired()) {
        if (!effectiveUserId && import.meta.env.DEV) console.log('⚠️ No user ID available')
        return
      }
      
      // Skip loading for legacy years - will show F&O placeholder
      if (isLegacyYear) {
        if (import.meta.env.DEV) console.log(`📊 Legacy year ${selectedYear} detected - skipping objective load`)
        setHasObjectives(false)
        setRoleKPIs([])
        setPerformancePlan(null)
        setIsLoading(false)
        return
      }
      
      // Prevent duplicate calls while already loading
      if (isLoadingPerformancePlan.current) {
        return
      }

      isLoadingPerformancePlan.current = true
      setIsLoading(true)
      setError(null)
      
      try {
        if (import.meta.env.DEV) console.log(`📅 Loading performance plan for user: ${effectiveUserId}, year: ${selectedYear}`)
        const plan = await performanceService.getEmployeePerformancePlan(effectiveUserId, selectedYear, false)
        
        // Debug: Check raw API response for Turnaround KPI
        const turnaroundObj = plan.objectives?.find((obj: any) => 
          obj.kpis?.some((k: any) => k.name === 'Turnaround time for issues')
        )
        if (turnaroundObj) {
          const turnaroundKPI = turnaroundObj.kpis.find((k: any) => k.name === 'Turnaround time for issues')
          if (import.meta.env.DEV) console.log('🔍 Raw API Response for Turnaround KPI:', {
            name: turnaroundKPI?.name,
            measurement_direction: turnaroundKPI?.measurement_direction,
            measurement_mode: turnaroundKPI?.measurement_mode,
            keys: Object.keys(turnaroundKPI || {})
          })
        }
        
        // Get campus from user data, not from employeeContext to avoid dependency
        const userCampus = user!.campus || 'Unknown Campus'
        
        setPerformancePlan(plan)
        
        if (plan.has_objectives && plan.objectives.length > 0) {
          // Filter out rejected and withdrawn objectives (pending_approval should be visible as read-only)
          const activeObjectives = plan.objectives.filter(obj => obj.status !== 'rejected' && obj.status !== 'withdrawn')
          
          // Set hasObjectives based on whether there are displayable objectives (not just API flag)
          // This prevents blank screen when all objectives are rejected
          setHasObjectives(activeObjectives.length > 0)
          
          // Convert employee objectives to RoleKPI format for existing components
          const allKPIs: RoleKPI[] = activeObjectives.flatMap(obj => 
            (obj.kpis || []).map(kpi => ({
              id: kpi.id,
              name: kpi.name,
              description: kpi.description || '',
              target_value: kpi.target_value,
              current_value: kpi.current_value || 0,
              unit: kpi.unit,
              frequency: kpi.frequency,
              status: kpi.status as 'on-track' | 'at-risk' | 'off-track',
              role_type: obj.role_type,
              functional_area: obj.functional_area,
              campus: userCampus,
              owner_id: user!.employeeId || null,
              cascade_to_objective_id: String(obj.id),
              cascade_to_kpa_id: obj.kpa ? String(obj.kpa.id) : null,
              cascade_to_pillar_id: obj.pillar ? String(obj.pillar.id) : null,
              kpa_id: obj.kpa?.id || null,
              pillar_id: obj.pillar?.id || null,
              strategic_objective_id: obj.strategic_objective?.id || null,
              kpa_name: obj.kpa?.name || null,
              pillar_name: obj.pillar?.name || null,
              strategic_objective_name: obj.strategic_objective?.name || null,
              individual_objective_id: obj.id,
              primary_initiative_id: null,
              // Include measurement configuration from API
              measurement_direction: (kpi as any).measurement_direction || 'higher_is_better',
              measurement_mode: (kpi as any).measurement_mode || 'numeric',
              rating_scale: (kpi as any).rating_scale,
              rating_options: (kpi as any).rating_options,
              min_value: (kpi as any).min_value,
              max_value: (kpi as any).max_value,
              midyear_value: (kpi as any).midyear_value,
              yearend_value: (kpi as any).yearend_value,
              individual_objective: {
                id: obj.id,
                title: obj.title,
                description: obj.description,
                role_type: obj.role_type,
                owner: user!.displayName || '',
                functional_area: obj.functional_area,
                status: obj.status,
                progress: obj.progress,
                start_date: obj.start_date,
                end_date: obj.end_date,
                strategic_objective_id: obj.strategic_objective?.id || 0,
                kpa_id: obj.kpa?.id || 0,
                pillar_id: obj.pillar?.id || 0,
                // Include full L1→L2→L3 strategic cascade
                strategic_objective: obj.strategic_objective,
                strategic_initiative: obj.strategic_initiative,
                operational_initiative: obj.operational_initiative,
                kpa: obj.kpa ? {
                  id: obj.kpa.id,
                  name: obj.kpa.name,
                  display_name: obj.kpa.display_name,
                  weight_percentage: obj.kpa.weight_percentage,
                  sort_order: (obj.kpa as any).sort_order
                } : undefined
              },
              primary_initiative: undefined,
              category: kpi.category,
              owner: user!.displayName,
              priority: kpi.priority || 'medium',
              progress_percentage: kpi.yearend_progress,
              midyear_progress: kpi.midyear_progress,
              yearend_progress: kpi.yearend_progress,
              trend: kpi.trend || 'stable',
              start_date: kpi.start_date,
              end_date: kpi.end_date,
              is_active: true,
              milestones: kpi.milestones || [],
              measurement_history: (kpi as any).measurement_history, // KPI-level centralized history
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }))
          )
          setRoleKPIs(allKPIs)
          
          if (activeObjectives.length > 0) {
            toast.success(`Loaded ${activeObjectives.length} objectives with ${allKPIs.length} KPIs`)
          }
        } else {
          // No objectives assigned yet - onboarding state
          setHasObjectives(false)
          setRoleKPIs([])
          if (import.meta.env.DEV) console.log('Onboarding state:', plan.message || 'No objectives assigned yet')
        }
        
        // Load review milestones (optional - not all users have them)
        try {
          const reviewData = await performanceService.getUserReviewMilestones(
            user!.employeeId ?? '',
            selectedYear,  // Use selected year
            true
          )
          setReviewMilestones(reviewData.milestones)
        } catch (reviewErr) {
          // Silently handle - review milestones are optional
          // 404 is expected for users without scheduled reviews
          const is404 = reviewErr instanceof Error && 
            (reviewErr.message.includes('404') || reviewErr.message.includes('Not Found'))
          if (!is404) {
            console.warn('Failed to load review milestones:', reviewErr)
          }
          // Always set empty array regardless of error type
          setReviewMilestones([])
        }
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load performance plan'
        setError(errorMessage)
        console.error('Failed to load employee performance plan:', err)
        toast.error(errorMessage)
        setRoleKPIs([])
        setPerformancePlan(null)
        setHasObjectives(false)
      } finally {
        setIsLoading(false)
        isLoadingPerformancePlan.current = false
      }
    }

    loadEmployeePerformancePlan()
  }, [effectiveUserId, selectedYear, isLegacyYear]) // Reload when year or user changes

  // Note: loadReviewCycle moved to ReviewsTab component

  // Load historical performance data from F&O for legacy years
  useEffect(() => {
    const loadHistoricalData = async () => {
      // Only load for legacy years - employeeId is the personnel number from F&O
      if (!isLegacyYear || !user?.employeeId) {
        setHistoricalData(null)
        return
      }

      setIsLoadingHistorical(true)
      try {
        if (import.meta.env.DEV) console.log(`📊 Loading F&O historical data for personnel #${user.employeeId}, year: ${selectedYear}`)
        const data = await integrationService.getHistoricalReviews(user.employeeId, selectedYear)
        setHistoricalData(data)
        if (import.meta.env.DEV) console.log(`✅ Loaded ${data.reviews.length} historical reviews from F&O`)
      } catch (error) {
        console.error('Failed to load historical performance data:', error)
        setHistoricalData(null)
        // Don't show error toast - historical data is optional
      } finally {
        setIsLoadingHistorical(false)
      }
    }

    loadHistoricalData()
  }, [user?.employeeId, selectedYear, isLegacyYear])

  // Auto-start interactive tour on first visit (after initial data load)
  useEffect(() => {
    if (!isLoading && !hasSeenTour && performancePlan) {
      // autoStartTour handles its own delay and cleanup
      return autoStartTour()
    }
  }, [isLoading, hasSeenTour, performancePlan, autoStartTour])

  // Load KPI snapshots for the selected review period and year
  useEffect(() => {
    const loadKPISnapshots = async () => {
      if (!user?.employeeId || sessionManager.isExpired()) return
      
      // Skip snapshot loading for legacy years
      if (isLegacyYear) {
        if (import.meta.env.DEV) console.log(`📊 Legacy year ${selectedYear} - skipping snapshot load`)
        setKpiSnapshots(new Map())
        return
      }

      try {
        const reviewType = selectedReviewPeriod === 'MIDYEAR' ? 'mid-year' : 'year-end'
        if (import.meta.env.DEV) console.log(`📸 Loading snapshots for ${user.employeeId} - ${reviewType} ${selectedYear}`)

        // Query user's review sessions to find the completed review for this period/year
        const sessions = await reviewService.getUserReviewSessions(
          user.employeeId,
          reviewType,
          undefined,
          selectedYear
        )
        if (import.meta.env.DEV) console.log(`📸 Found ${sessions.length} sessions:`, sessions)

        // Find the completed review session for this period
        const completedSession = sessions.find((s: any) => s.status === 'completed')
        if (import.meta.env.DEV) console.log('📸 Completed session:', completedSession)

        if (!completedSession) {
          if (import.meta.env.DEV) console.log(`📸 No completed ${reviewType} review found for ${selectedYear}`)
          setKpiSnapshots(new Map())
          return
        }

        // Load full session details with KPA ratings by UUID
        if (import.meta.env.DEV) console.log(`📸 Loading full session: ${completedSession.id}`)
        const session = await reviewService.getReviewSession(completedSession.id)
        if (import.meta.env.DEV) console.log('📸 Session KPA ratings:', session.kpa_ratings)

        if (session.kpa_ratings && session.kpa_ratings.length > 0) {
          // Build snapshot map: KPA ID -> (KPI ID -> snapshot value)
          const snapshotMap = new Map<string, Map<string, number>>()

          session.kpa_ratings.forEach((rating: any) => {
            if (rating.kpi_snapshot && rating.kpi_snapshot.kpis) {
              const kpiMap = new Map<string, number>()
              rating.kpi_snapshot.kpis.forEach((kpi: any) => {
                kpiMap.set(kpi.kpi_id, kpi.current_value)
              })
              // Store ONLY by numeric KPA ID (single source of truth)
              if (rating.kpa_id) {
                snapshotMap.set(String(rating.kpa_id), kpiMap)
                if (import.meta.env.DEV) console.log(`📸 Loaded ${rating.kpi_snapshot.kpis.length} KPI snapshots for KPA ID: ${rating.kpa_id}`)
              }
            }
          })

          setKpiSnapshots(snapshotMap)
          if (import.meta.env.DEV) console.log(`✅ Loaded KPI snapshots for ${reviewType} ${selectedYear}:`, snapshotMap)
        } else {
          if (import.meta.env.DEV) console.log('📸 No KPA ratings or snapshots in session')
          setKpiSnapshots(new Map())
        }
      } catch (error) {
        console.error('❌ Failed to load KPI snapshots:', error)
        setKpiSnapshots(new Map())
      }
    }

    loadKPISnapshots()
  }, [user?.employeeId, selectedReviewPeriod, selectedYear, isLegacyYear])

  const handleCloseWorkspace = () => {
    setIsWorkspaceOpen(false)
    setSelectedObjective(null)
  }

  const handleGenerateAISuggestions = () => {
    setIsAIGenerating(true)
    setTimeout(() => {
      setIsAIGenerating(false)
      setShowAISuggestions(true)
    }, 2000)
  }

  const handleAddEvidence = (evidence: Omit<EvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => {
    // Implementation would save evidence to backend
    if (import.meta.env.DEV) console.log('Adding evidence:', evidence)
  }

  const handleAddMilestone = async (milestone: {
    title: string
    description: string
    due_date: string
    milestone_type: string
  }) => {
    if (!selectedObjective) return

    try {
      // Add milestone to backend
      const newMilestone = await performanceService.addKPIMilestone(selectedObjective.id, {
        title: milestone.title,
        description: milestone.description,
        due_date: milestone.due_date,
        milestone_type: milestone.milestone_type as 'strategic' | 'operational' | 'individual'
      })

      // Update local state to show the new milestone immediately
      const updatedObjective = {
        ...selectedObjective,
        milestones: [...selectedObjective.milestones, newMilestone]
      }

      // Update the selected objective
      setSelectedObjective(updatedObjective)

      // Update the roleKPIs array to reflect the change
      setRoleKPIs(prevKPIs => 
        prevKPIs.map(kpi => 
          kpi.id === selectedObjective.id ? updatedObjective : kpi
        )
      )

      if (import.meta.env.DEV) console.log('Milestone created successfully:', newMilestone)
    } catch (error) {
      console.error('Failed to create milestone:', error)
      // You could add a toast notification here for user feedback
    }
  }

  const handleUpdateMilestone = async (milestoneId: string, milestone: {
    title?: string
    description?: string
    due_date?: string
    milestone_type?: string
    status?: string
    progress?: number
  }) => {
    if (!selectedObjective) return

    try {
      // Update milestone in backend
      const updatedMilestone = await performanceService.updateKPIMilestone(selectedObjective.id, milestoneId, milestone as any)

      // Update local state - completely replace the milestone with the backend response
      const updatedObjective = {
        ...selectedObjective,
        milestones: selectedObjective.milestones.map((m: any) => 
          m.id === milestoneId ? updatedMilestone : m
        )
      }

      // Update the selected objective
      setSelectedObjective(updatedObjective)

      // Update the roleKPIs array to reflect the change
      setRoleKPIs(prevKPIs => 
        prevKPIs.map(kpi => 
          kpi.id === selectedObjective.id ? updatedObjective : kpi
        )
      )

      //console.log('Milestone updated successfully:', updatedMilestone)
    } catch (error) {
      console.error('Failed to update milestone:', error)
    }
  }

  const handleDeleteMilestone = async (milestoneId: string) => {
    if (!selectedObjective) return

    try {
      // Delete milestone from backend
      await performanceService.deleteKPIMilestone(selectedObjective.id, milestoneId)

      // Update local state
      const updatedObjective = {
        ...selectedObjective,
        milestones: selectedObjective.milestones.filter((m: any) => m.id !== milestoneId)
      }

      // Update the selected objective
      setSelectedObjective(updatedObjective)

      // Update the roleKPIs array to reflect the change
      setRoleKPIs(prevKPIs => 
        prevKPIs.map(kpi => 
          kpi.id === selectedObjective.id ? updatedObjective : kpi
        )
      )

      //console.log('Milestone deleted successfully:', milestoneId)
    } catch (error) {
      console.error('Failed to delete milestone:', error)
    }
  }

  const handleGenerateInsights = () => {
    setIsAILoading(true)
    // Implementation would call AI service
    setTimeout(() => setIsAILoading(false), 2000)
  }


  // Load evidence from backend, scoped to the currently-selected performance
  // year so 2026 entries don't leak into a 2025 mid-year view (same period
  // label, different year). Without the year scope, EmployeeKPAView would
  // show evidence authored in any year while the rest of the page is filtered
  // to a single year.
  const loadEvidence = useCallback(async (userId: string, year?: number) => {
    if (sessionManager.isExpired()) return
    try {
      setIsLoadingEvidence(true)
      const response = await performanceService.getUserEvidence(userId, year ? { year } : undefined)
      setEnhancedEvidence(response.evidence || [])
    } catch (error) {
      console.error('Failed to load evidence:', error)
      toast.error('Failed to load entries. Please try again.')
    } finally {
      setIsLoadingEvidence(false)
    }
  }, [])

  // Note: loadFeedbackData moved to FeedbackTab component

  // Reload evidence whenever the user OR selectedYear changes. The review
  // period (mid-year vs year-end) is filtered client-side from this superset.
  useEffect(() => {
    if (!user?.employeeId) return
    loadEvidence(user.employeeId, selectedYear)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.employeeId, selectedYear])

  // Note: Feedback data loading moved to FeedbackTab component

  // KPA-specific handlers
  const handleAddEvidenceEnhanced = async (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>): Promise<EnhancedEvidenceEntry | undefined> => {
    if (!user?.employeeId) return undefined
    const userId = user.employeeId
    
    try {
      const response = await performanceService.createEvidence(userId, evidence)
      
      // If evidence has KPI measurement, record it to update employee's KPI progress
      if (evidence.kpiId && evidence.measurementValue !== undefined) {
        try {
          await performanceService.recordEmployeeKPIMeasurement(userId, evidence.kpiId, {
            value: evidence.measurementValue,
            notes: evidence.description || evidence.title,
            evidence_id: response.evidence?.id,
            period: evidence.reviewPeriod  // Pass the review period
          })
          toast.success(`Your KPI progress updated to ${evidence.measurementValue}`)
          
          // Optimistically update KPI value in local state instead of full reload
          if (evidence.kpiId) {
            setRoleKPIs(prev => prev.map(kpi => 
              kpi.id === evidence.kpiId
                ? { ...kpi, current_value: evidence.measurementValue ?? kpi.current_value }
                : kpi
            ))
            
            // Also update performance plan if it exists
            if (performancePlan) {
              setPerformancePlan(prevPlan => {
                if (!prevPlan) return prevPlan
                return {
                  ...prevPlan,
                  objectives: prevPlan.objectives.map(obj => ({
                    ...obj,
                    kpis: obj.kpis?.map(kpi =>
                      kpi.id === evidence.kpiId
                        ? { ...kpi, current_value: evidence.measurementValue ?? kpi.current_value }
                        : kpi
                    ) || []
                  }))
                }
              })
            }
            
            // Refetch to get updated measurement history
            await refetchPerformancePlan()
          }
        } catch (measurementError) {
          console.error('Failed to record KPI measurement:', measurementError)
          // Don't fail the whole operation, evidence was still created
          if (import.meta.env.DEV) console.log('Evidence added but KPI measurement update failed')
        }
      }
      
      // Add to local state
      setEnhancedEvidence(prev => [response.evidence, ...prev])
      
      toast.success('Entry added successfully')
      return response.evidence // Return created evidence for attachment upload
    } catch (error) {
      console.error('Failed to add evidence:', error)
      toast.error('Failed to add entry. Please try again.')
      return undefined
    }
  }

  // Update evidence locally without API call (for attachments)
  const handleUpdateEvidenceLocal = (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => {
    setEnhancedEvidence(prev => prev.map(ev => 
      ev.id === evidenceId ? { ...ev, ...updates } : ev
    ))
  }

  const handleUpdateEvidence = async (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => {
    if (!user?.employeeId) return
    const userId = user.employeeId
    
    try {
      const response = await performanceService.updateEvidence(userId, evidenceId, updates)
      
      // Update local evidence state with full API response
      // This ensures visibility, updatedAt, and other fields are correct
      if (response?.evidence) {
        setEnhancedEvidence(prev => prev.map(ev => 
          ev.id === evidenceId ? response.evidence : ev
        ))
      } else {
        // Fallback: merge updates if no response
        setEnhancedEvidence(prev => prev.map(ev => 
          ev.id === evidenceId ? { ...ev, ...updates } : ev
        ))
      }
      
      // If measurement value was updated, record KPI measurement to update period-specific values
      if (updates.kpiId && updates.measurementValue !== undefined) {
        try {
          // Determine review period from updates or response
          const reviewPeriod = updates.reviewPeriod || response?.evidence?.reviewPeriod || selectedReviewPeriod
          
          if (import.meta.env.DEV) console.log('📊 Recording KPI measurement:', {
            kpiId: updates.kpiId,
            value: updates.measurementValue,
            period: reviewPeriod
          })
          
          await performanceService.recordEmployeeKPIMeasurement(userId, updates.kpiId, {
            value: updates.measurementValue,
            notes: updates.description || updates.title || 'Measurement update',
            evidence_id: evidenceId,
            period: reviewPeriod
          })
        } catch (err) {
          console.error('Failed to record KPI measurement:', err)
        }
        
        // Optimistically update local KPI state
        setRoleKPIs(prev => prev.map(kpi => 
          kpi.id === updates.kpiId
            ? { ...kpi, current_value: updates.measurementValue ?? kpi.current_value }
            : kpi
        ))
        
        // Also update performance plan if it exists
        if (performancePlan) {
          setPerformancePlan(prevPlan => {
            if (!prevPlan) return prevPlan
            return {
              ...prevPlan,
              objectives: prevPlan.objectives.map(obj => ({
                ...obj,
                kpis: obj.kpis?.map(kpi =>
                  kpi.id === updates.kpiId
                    ? { ...kpi, current_value: updates.measurementValue ?? kpi.current_value }
                    : kpi
                ) || []
              }))
            }
          })
        }
        
        toast.success(`Entry and KPI progress updated to ${updates.measurementValue}`)
        
        // Refetch to get updated measurement history and period-specific values
        await refetchPerformancePlan()
      } else {
        toast.success('Entry updated successfully')
      }
    } catch (error) {
      console.error('Failed to update evidence:', error)
      toast.error('Failed to update entry. Please try again.')
    }
  }

  const handleUpdateEvidenceClassification = async (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => {
    if (!user?.employeeId) return
    const userId = user.employeeId
    
    try {
      await performanceService.updateEvidenceClassification(userId, evidenceId, classification)
      
      // Update local state
      setEnhancedEvidence(prev => prev.map(ev => 
        ev.id === evidenceId ? { ...ev, classification } : ev
      ))
      
      toast.success('Classification updated')
    } catch (error) {
      console.error('Failed to update evidence classification:', error)
      toast.error('Failed to update classification. Please try again.')
    }
  }

  const handleDeleteEvidence = async (evidenceId: string) => {
    if (!user?.employeeId) return
    const userId = user.employeeId

    try {
      await performanceService.deleteEvidence(userId, evidenceId)

      // Remove from local state
      setEnhancedEvidence(prev => prev.filter(ev => ev.id !== evidenceId))

      // Refetch the performance plan so KPI progress reflects the rolled-back
      // measurement (backend rolls back EmployeeKPIProgress when measurement-bearing
      // evidence is deleted). Without this, KPI progress shows stale values until
      // the page is reloaded.
      await refetchPerformancePlan()

      toast.success('Entry deleted successfully')
    } catch (error) {
      console.error('Failed to delete evidence:', error)
      toast.error('Failed to delete entry. Please try again.')
    }
  }

  const handleUploadDocument = async (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => {
    console.log('📤 handleUploadDocument called with file:', file?.name, 'size:', file?.size)
    if (!user?.employeeId) {
      console.error('❌ No employeeId, cannot upload')
      return
    }
    
    try {
      // 1. Create evidence entry first
      console.log('📝 Creating evidence entry...')
      const response = await performanceService.createEvidence(user.employeeId, {
        ...evidenceData,
        type: 'document' as const,
        reviewPeriod: evidenceData.reviewPeriod || (selectedReviewPeriod === 'MIDYEAR' ? 'MIDYEAR' : 'YEAREND')
      } as any)
      console.log('✅ Evidence created, response:', response)
      
      // 2. Upload the file as an attachment to the created evidence
      // Backend returns { evidence: { id: "..." } }, so access nested evidence object
      const evidenceId = response?.evidence?.id || response?.id
      console.log('🔍 Extracted evidenceId:', evidenceId)
      
      if (evidenceId && file) {
        console.log('📎 Uploading attachment to evidence:', evidenceId)
        await performanceService.uploadAttachment(evidenceId, file)
        console.log('✅ Attachment uploaded successfully')
      } else {
        console.error('❌ Cannot upload attachment - evidenceId:', evidenceId, 'file:', file)
      }
      
      // 3. Reload evidence data
      await loadEvidence(user.employeeId, selectedYear)
      
      toast.success('Document uploaded successfully')
    } catch (error) {
      console.error('Failed to upload document:', error)
      toast.error('Failed to upload document')
    }
  }


  const handleCreateObjective = async (objective: any) => {
    if (import.meta.env.DEV) console.log('Creating individual objective:', objective)
    
    if (!user || !user.employeeId) {
      toast.error('User not authenticated. Please log in again.')
      return
    }
    
    try {
      // Create the objective
      await performanceService.createEmployeeObjective(
        user.employeeId,
        {
          title: objective.title,
          description: objective.description,
          kpa_id: objective.kpaId || objective.kpa_id,
          start_date: objective.startDate || objective.start_date,
          end_date: objective.endDate || objective.end_date,
          kpis: objective.kpis || []
        }
      )
      
      toast.success('Objective created! Submitted for people leader approval.')
      
      // Create collaborative session for manager to review
      if (user.managerId) {
        try {
          await performanceService.createCollaborativeSession({
            employee_id: user.employeeId,
            employee_name: user.displayName || 'Team Member',
            employee_role: user.jobTitle || '',
            manager_id: user.managerEmployeeId || user.managerId,
            manager_name: 'People Leader',
            session_type: 'objective_revision',
            performance_year: new Date().getFullYear(),
            scheduled_by: 'system'
          })
          if (import.meta.env.DEV) console.log('Collaborative session created for objective review')
          await refetchCollaborativeSessions()
        } catch (sessionError) {
          if (import.meta.env.DEV) console.log('Note: Collaborative session may already exist', sessionError)
          await refetchCollaborativeSessions()
        }
      }
      
      // Reload objectives
      await refetchPerformancePlan()
    } catch (error) {
      console.error('Failed to create objective:', error)
      toast.error('Failed to create objective. Please try again.')
    }
  }

  const handleUpdateObjective = async (objectiveId: number, updates: any) => {
    if (import.meta.env.DEV) console.log('Updating objective:', objectiveId, updates)
    
    if (!user || !user.employeeId) {
      toast.error('User not authenticated. Please log in again.')
      return
    }
    
    try {
      // Update the objective
      const saved = await performanceService.updateIndividualObjective(objectiveId, updates)
      // An unchanged objective stays active: nothing to review (HR UAT list #9).
      const sentForApproval = saved?.status === 'pending_approval'

      toast.success(sentForApproval
        ? 'Objective updated! Changes submitted for people leader review.'
        : 'Objective saved. Nothing changed, so it stays as agreed.')

      // Create collaborative session for manager to review changes
      if (user.managerId && sentForApproval) {
        try {
          await performanceService.createCollaborativeSession({
            employee_id: user.employeeId,
            employee_name: user.displayName || 'Team Member',
            employee_role: user.jobTitle || '',
            manager_id: user.managerEmployeeId || user.managerId,
            manager_name: 'People Leader',
            session_type: 'objective_revision',
            performance_year: new Date().getFullYear(),
            scheduled_by: 'system'
          })
          if (import.meta.env.DEV) console.log('Collaborative session created for objective changes review')
          await refetchCollaborativeSessions()
        } catch (sessionError) {
          if (import.meta.env.DEV) console.log('Note: Collaborative session may already exist', sessionError)
          await refetchCollaborativeSessions()
        }
      }
      
      // Reload objectives
      await refetchPerformancePlan()
    } catch (error) {
      console.error('Failed to update objective:', error)
      toast.error('Failed to update objective. Please try again.')
    }
  }

  const handleDeleteObjective = (objectiveId: number) => {
    if (import.meta.env.DEV) console.log('Deleting objective:', objectiveId)
  }

  const handleViewObjectiveDetails = (objective: any) => {
    if (import.meta.env.DEV) console.log('Viewing objective details:', objective)
  }

  const handleViewKPIDetails = (kpi: any) => {
    if (import.meta.env.DEV) console.log('Viewing KPI details:', kpi)
  }

  // Transform API data into KPA structure
  const transformAPIDataToKPAs = (roleKPIs: RoleKPI[], snapshots: Map<string, Map<string, number>>, evidenceData: EnhancedEvidenceEntry[]): KPAData[] => {
    // Helper to get snapshot value by KPA ID and KPI ID
    const getSnapshotValue = (kpaId: string | null, kpiId: string): number | null => {
      if (!kpaId) return null
      const kpaSnapshotMap = snapshots.get(kpaId)
      if (kpaSnapshotMap) {
        return kpaSnapshotMap.get(kpiId) ?? null
      }
      return null
    }

    // Group KPIs by KPA name
    const kpaGroups = roleKPIs.reduce((acc, kpi) => {
      const kpaName = (kpi as any).kpa_name || 'Uncategorized'
      if (!acc[kpaName]) {
        acc[kpaName] = []
      }
      acc[kpaName].push(kpi)
      return acc
    }, {} as Record<string, RoleKPI[]>)

    // Convert each group to KPA structure
    return Object.entries(kpaGroups).map(([kpaName, kpis], index) => {
      // Extract KPA ID and sort_order from the first KPI's objective
      const kpaId = kpis[0]?.individual_objective?.kpa?.id 
        ? String(kpis[0].individual_objective.kpa.id) 
        : null
      const kpaSortOrder = (kpis[0]?.individual_objective?.kpa as any)?.sort_order ?? 999
      // Get unique individual objectives from the KPIs
      const objectiveMap = new Map()
      const underlyingKPIs: any[] = []
      let kpaDisplayName: string | undefined
      let pillarName: string | undefined
      let strategicObjectiveName: string | undefined
      
      kpis.forEach(kpi => {
        // Check if we have a snapshot value for this KPI
        const snapshotValue = getSnapshotValue(kpaId, (kpi as any).id)
        
        // Carry-forward eligibility: a KPI carries its Mid-Year value into the
        // Year-End view when its objective extends past June (end_date Jul–Dec)
        // OR has no end_date (open-ended = full-year).
        const objEndDate = (kpi as any).individual_objective?.end_date
        const carryForwardEligible = !objEndDate || new Date(objEndDate).getMonth() >= 6

        // Use period-specific values based on selectedReviewPeriod
        // Priority: snapshot > period value > (carried Mid-Year) > 0
        let currentValue: number
        let carriedFromMidYear = false
        if (snapshotValue !== null) {
          currentValue = snapshotValue
          if (import.meta.env.DEV) console.log(`📸 Using snapshot for KPI "${kpi.name}": ${snapshotValue}`)
        } else {
          const midyearVal = (kpi as any).midyear_value
          const yearendVal = (kpi as any).yearend_value
          if (selectedReviewPeriod === 'MIDYEAR') {
            currentValue = midyearVal ?? 0
          } else if ((yearendVal === null || yearendVal === undefined) && carryForwardEligible && midyearVal !== null && midyearVal !== undefined) {
            currentValue = midyearVal
            carriedFromMidYear = true
          } else {
            currentValue = yearendVal ?? 0
          }
          if (import.meta.env.DEV) console.log(`📊 KPI "${kpi.name}": midyear=${midyearVal}, yearend=${yearendVal}, using=${currentValue} for ${selectedReviewPeriod}${carriedFromMidYear ? ' (carried)' : ''}`)
        }

        // Add to underlying KPIs (preserve original fields for reference)
        const measurementDirection = (kpi as any).measurement_direction || 'higher_is_better'
        if (kpi.name === 'Turnaround time for issues') {
          if (import.meta.env.DEV) console.log(`🔍 Creating underlyingKPI "${kpi.name}": API measurement_direction=${(kpi as any).measurement_direction}, using=${measurementDirection}`)
        }
        underlyingKPIs.push({
          id: (kpi as any).id,
          name: kpi.name,
          description: kpi.description,
          target_value: (kpi as any).target_value,
          current_value: currentValue,
          midyear_value: (kpi as any).midyear_value,
          yearend_value: (kpi as any).yearend_value,
          carriedFromMidYear,
          carryForwardEligible,
          unit: (kpi as any).unit || '',
          status: kpi.status,
          owner: (kpi as any).owner,
          functional_area: kpi.functional_area,
          frequency: (kpi as any).frequency || 'monthly',
          milestones: (kpi as any).milestones || [],
          measurement_history: (kpi as any).measurement_history,  // KPI-level centralized history
          // Include measurement configuration for accurate progress calculation
          measurement_direction: measurementDirection,
          measurement_mode: (kpi as any).measurement_mode || 'numeric',
          rating_scale: (kpi as any).rating_scale,
          rating_options: (kpi as any).rating_options,
          min_value: (kpi as any).min_value,
          max_value: (kpi as any).max_value
        })

        // Add individual objective if it exists
        const individualObj = (kpi as any).individual_objective
        
        // Extract hierarchy information from KPI
        if (!kpaDisplayName && individualObj?.kpa?.display_name) {
          kpaDisplayName = individualObj.kpa.display_name
        }
        if (!pillarName && (kpi as any).pillar_name) {
          pillarName = (kpi as any).pillar_name
        }
        if (!strategicObjectiveName && (kpi as any).strategic_objective_name) {
          strategicObjectiveName = (kpi as any).strategic_objective_name
        }
        
        // Store individual objective with hierarchy information
        if (individualObj && !objectiveMap.has(individualObj.id)) {
          objectiveMap.set(individualObj.id, {
            id: individualObj.id,
            title: individualObj.title,
            description: individualObj.description,
            role_type: individualObj.role_type,
            owner: individualObj.owner,
            functional_area: individualObj.functional_area,
            status: individualObj.status,
            progress: individualObj.progress,
            start_date: individualObj.start_date,
            end_date: individualObj.end_date,
            strategic_objective_id: individualObj.strategic_objective_id,
            kpa_id: individualObj.kpa_id,
            pillar_id: individualObj.pillar_id,
            pillar_name: (kpi as any).pillar_name,
            strategic_objective_name: (kpi as any).strategic_objective_name,
            // Include full strategic context (L1→L2→L3)
            strategic_objective: individualObj.strategic_objective,
            strategic_initiative: individualObj.strategic_initiative,
            operational_initiative: individualObj.operational_initiative,
            kpis: [] // Initialize empty kpis array
          })
        }
        
        // Add this KPI to the objective's kpis array (avoid duplicates)
        if (individualObj && objectiveMap.has(individualObj.id)) {
          const obj = objectiveMap.get(individualObj.id)
          if (!obj.kpis) obj.kpis = []
          
          // Only add if KPI doesn't already exist
          const kpiId = (kpi as any).id
          if (!obj.kpis.some((existingKpi: any) => existingKpi.id === kpiId)) {
            const measurementDirection = (kpi as any).measurement_direction || 'higher_is_better'
            if (kpi.name === 'Turnaround time for issues') {
              if (import.meta.env.DEV) console.log(`🔍 Creating objective KPI "${kpi.name}": API measurement_direction=${(kpi as any).measurement_direction}, using=${measurementDirection}`)
            }
            obj.kpis.push({
              id: kpiId,
              name: kpi.name,
              description: kpi.description,
              target_value: (kpi as any).target_value,
              current_value: currentValue,  // Period-specific value based on selectedReviewPeriod
              midyear_value: (kpi as any).midyear_value,
              yearend_value: (kpi as any).yearend_value,
              carriedFromMidYear,
              carryForwardEligible,
              unit: (kpi as any).unit || '',
              status: kpi.status,
              frequency: (kpi as any).frequency || 'monthly',
              trend: (kpi as any).trend,
              milestones: (kpi as any).milestones || [],
              measurement_history: (kpi as any).measurement_history,  // KPI-level centralized history
              // Include measurement configuration for accurate progress calculation
              measurement_direction: measurementDirection,
              measurement_mode: (kpi as any).measurement_mode || 'numeric',
              rating_scale: (kpi as any).rating_scale,
              rating_options: (kpi as any).rating_options,
              min_value: (kpi as any).min_value,
              max_value: (kpi as any).max_value
            })
          }
        }
      })

      const individualObjectives = Array.from(objectiveMap.values()).map(obj => ({
        ...obj,
        // Deduplicate KPIs in each objective's kpis array
        kpis: obj.kpis ? Array.from(
          new Map(obj.kpis.map((kpi: any) => [kpi.id, kpi])).values()
        ) : []
      }))
      
      // Calculate progress average based on KPI achievement (matching MemberKPAPanel logic)
      let totalProgress = 0
      let progressCount = 0
      
      individualObjectives.forEach(obj => {
        if (obj.kpis && obj.kpis.length > 0) {
          // Calculate objective progress from its KPIs using enterprise calculation
          const kpiProgressSum = obj.kpis.reduce((sum: number, kpi: any) => {
            // Get measurement configuration from original KPI data
            const originalKpi = kpis.find(k => k.id === kpi.id)
            const mode = ((originalKpi as any)?.measurement_mode || 'numeric') as MeasurementMode
            const direction = ((originalKpi as any)?.measurement_direction || 'higher_is_better') as MeasurementDirection
            
            // Use enterprise progress calculation
            const progressResult = calculateProgress(
              kpi.current_value,
              kpi.target_value,
              direction,
              mode
            )
            return sum + progressResult.progress
          }, 0)
          totalProgress += kpiProgressSum / obj.kpis.length
          progressCount++
        } else if (obj.progress !== undefined) {
          // Fallback to objective.progress if no KPIs
          totalProgress += obj.progress
          progressCount++
        }
      })
      
      const averageProgress = progressCount > 0 ? Math.round(totalProgress / progressCount) : 0
      
      // Determine risk level based on KPI statuses
      const atRiskCount = kpis.filter(kpi => kpi.status === 'at-risk').length
      const offTrackCount = kpis.filter(kpi => kpi.status === 'off-track').length
      const riskLevel = 
        offTrackCount > 0 || atRiskCount > kpis.length / 2 ? 'high' :
        atRiskCount > 0 ? 'medium' : 'low'

      // Calculate weight (simplified - in real app this would come from API)
      const weights = [35, 25, 20, 20]
      const weight = weights[index] || Math.round(100 / Object.keys(kpaGroups).length)
      
      // Use consistent name-based ID as fallback if kpaId is null
      const finalKpaId = kpaId || kpaName.toLowerCase().replace(/\s+/g, '-')

      return {
        id: finalKpaId,  // Use numeric ID from API or name-based ID as fallback
        name: kpaName,
        display_name: kpaDisplayName, // Use display_name from API if available
        description: getKpaDescription(kpaDisplayName || kpaName),
        strategic_pillar_id: String(index + 1),
        weight_percentage: weight,
        sort_order: kpaSortOrder, // Include sort_order for consistent KPA ordering
        individualObjectives,
        underlyingKPIs,
        totalEvidence: evidenceData.filter(e => 
          e.kpaId === finalKpaId || 
          kpis.some(kpi => e.kpiId === (kpi as any).id) ||
          individualObjectives.some(obj => e.objectiveId === String(obj.id))
        ).length,
        averageProgress,
        riskLevel: riskLevel as 'low' | 'medium' | 'high',
        // No synthetic next-review date — a computed today+Nd value showed up
        // as a fake "NEXT REFLECTION · <date>" dateline on every KPA. The
        // display components gate on a real value and fall back to
        // "Not scheduled". See kpaTransformers.ts for the matching fix.
        nextReview: undefined
      }
    })
    // Sort KPAs by sort_order for consistent ordering
    // Order: 1. Operating Financial, 2. Internal Business, 3. Culture, 4. Customer and Stakeholder
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999))
  }

  // Get KPA data from the current roleKPIs
  // Re-calculate when selectedReviewPeriod changes to apply period-specific values
  const kpaData = useMemo(() => {
    if (!Array.isArray(roleKPIs) || roleKPIs.length === 0) {
      return []
    }
    if (import.meta.env.DEV) console.log(`🔄 Recalculating kpaData for ${selectedReviewPeriod} period`)
    return transformAPIDataToKPAs(roleKPIs, kpiSnapshots, enhancedEvidence)
  }, [roleKPIs, kpiSnapshots, selectedReviewPeriod, enhancedEvidence])

  // Carry-eligible Mid-Year measurements surfaced in the Year-End view as
  // read-only "carried forward" context (continuing objectives).
  const carriedMidYearEvidence = useMemo(
    () => buildCarriedMidYearEvidence(enhancedEvidence, kpaData, selectedReviewPeriod),
    [enhancedEvidence, kpaData, selectedReviewPeriod],
  )

  // The top-level "my Impact Portfolio" is cross-period (shows all entries), so
  // the Mid-Year measurement is already present — we TAG it in place (rather than
  // append a copy) so it carries the "Carried forward from Mid-Year" badge in the
  // Year-End view without duplicating. (Period-filtered surfaces append instead.)
  const portfolioTabEvidence = useMemo(() => {
    if (carriedMidYearEvidence.length === 0) return enhancedEvidence
    const carriedIds = new Set(carriedMidYearEvidence.map(e => e.id))
    return enhancedEvidence.map(e => (carriedIds.has(e.id) ? { ...e, carriedFromMidYear: true } : e))
  }, [enhancedEvidence, carriedMidYearEvidence])


  // Get objective statistics. NOTE: `roleKPIs` is a *flattened* list of
  // KPIs across active objectives, not a list of objectives. Computing
  // `total` from `roleKPIs.length` produced the well-known "17 objectives"
  // mislabel where the tab subtitle reported KPI counts as objective counts.
  // `total` now derives from the deduplicated set of `individualObjectives`
  // ids on `kpaData` (which is already filtered to non-rejected /
  // non-withdrawn). KPI-level health metrics still come off `roleKPIs`.
  const objectiveStats = useMemo(() => {
    const objectiveIds = new Set<number>()
    for (const kpa of kpaData) {
      for (const obj of (kpa.individualObjectives || [])) {
        if (obj && obj.id != null) objectiveIds.add(obj.id)
      }
    }

    return {
      total: objectiveIds.size,
      onTrack: roleKPIs.filter(k => k.status === 'on-track').length,
      atRisk: roleKPIs.filter(k => k.status === 'at-risk').length,
      offTrack: roleKPIs.filter(k => k.status === 'off-track').length,
    }
  }, [kpaData, roleKPIs])

  // Filter evidence by selected review period
  const filteredEvidence = useMemo(() => {
    if (import.meta.env.DEV) console.log('🔍 Evidence Filter Debug:', {
      selectedReviewPeriod,
      totalEvidence: enhancedEvidence.length,
      evidenceByPeriod: {
        MIDYEAR: enhancedEvidence.filter(e => e.reviewPeriod === 'MIDYEAR').length,
        YEAREND: enhancedEvidence.filter(e => e.reviewPeriod === 'YEAREND').length,
        noPeriod: enhancedEvidence.filter(e => !e.reviewPeriod).length
      }
    })
    
    // If no review period filter is set, show all evidence
    if (!selectedReviewPeriod) {
      return enhancedEvidence
    }
    
    // Filter evidence by review period. Three buckets surface:
    //   1. Legacy evidence with no `reviewPeriod` (shown in both views)
    //   2. Evidence whose originating period matches the current view
    //   3. Evidence opted into both periods via `appliesToBothPeriods`
    const filtered = enhancedEvidence.filter(evidence =>
      !evidence.reviewPeriod ||
      evidence.reviewPeriod === selectedReviewPeriod ||
      evidence.appliesToBothPeriods === true
    )
    
    if (import.meta.env.DEV) console.log('📊 Filtered Result:', {
      selectedPeriod: selectedReviewPeriod,
      filteredCount: filtered.length,
      filteredItems: filtered.map(e => ({ id: e.id, title: e.title, period: e.reviewPeriod }))
    })
    
    // Append carried Mid-Year measurements (Year-End view, continuing objectives)
    // so per-KPA portfolios show them as read-only "carried forward" context.
    return [...filtered, ...carriedMidYearEvidence]
  }, [enhancedEvidence, selectedReviewPeriod, carriedMidYearEvidence])

  // Load profile data when profile manager is opened
  const handleOpenProfileManager = async () => {
    // Always fetch fresh data from API
    setLoadingProfileData(true)
    setShowProfileManager(true)
    // Both parts load side by side, and one failing doesn't hide the other: a
    // failed profile hid the F&O career history too (HR UAT list #8).
    const { details: completeProfile, career: careerHistory, problem } = await loadProfileParts(
      () => profileService.getCompleteProfile(),
      () => profileService.getInternalCareerHistory(),
    )
    setInternalCareerHistory(careerHistory)
    if (problem) toast.error(problem)
    try {
      if (!completeProfile) throw new Error('Profile details did not load')

      // Transform API data to AnnualProfileManager format
      const transformedData = {
        currentRole: {
          jobTitle: employeeContext.jobGroup || 'Unknown',
          department: employeeContext.department || 'Unknown',
          campus: employeeContext.campus || 'Unknown',
          lineManager: employeeContext.reportingManager || 'Unknown',
          employmentDate: new Date().toISOString() // TODO: Get from F&O data when available
        },
        qualifications: completeProfile.qualifications.map(q => ({
          id: String(q.id),
          type: q.qualification_type as any,
          institution: q.institution,
          country: q.country || 'south_africa',
          qualificationName: q.qualification_name,
          cesmCategory: q.cesm_category as any,
          fieldOfStudy: q.field_of_study,
          nqfLevel: q.nqf_level as any,
          yearCompleted: q.year_completed || '',
          inProgress: q.in_progress,
          expectedCompletionYear: q.expected_completion_year,
          internationalEquivalency: q.international_equivalency as any,
          saqaEvaluated: q.saqa_evaluated,
          saqaReferenceNumber: q.saqa_reference_number,
          professionalBody: q.professional_body,
          membershipNumber: q.membership_number,
          membershipStatus: q.membership_status,
          thesis: q.thesis,
          thesisTitle: q.thesis_title,
          verificationDocument: q.verification_document_url
        })),
        externalCareerHistory: completeProfile.career_history.map(c => ({
          id: String(c.id),
          organization: c.organization,
          position: c.position,
          startDate: c.start_date,
          endDate: c.end_date,
          directReports: c.direct_reports || 0,
          keyAchievements: (c.key_achievements || []).join(', '),
          skillsDeveloped: c.skills_developed || []
        })),
        professionalContext: {
          areasOfExpertise: completeProfile.professional_context?.areas_of_expertise || [],
          languageProficiencies: (completeProfile.professional_context?.language_proficiencies || []).map(l => ({
            language: l.language,
            proficiency: l.level
          })),
          geographicExperience: completeProfile.professional_context?.geographic_experience || [],
          industryExperience: completeProfile.professional_context?.industry_experience || []
        },
        lastUpdated: new Date().toISOString(),
        completionStatus: completeProfile.professional_context?.completion_status === 'complete' ? 'complete' : 'incomplete',
        nextUpdateDue: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year from now
      }
      
      setEmployeeProfileData(transformedData)
    } catch (error) {
      console.error('Failed to load profile data:', error)
      // Details that arrived but couldn't be shown: say so, as for a failed load.
      if (completeProfile) toast.error(profileProblem(true, careerHistory === null) as string)
      // Create empty profile data structure so the form can still work
      const emptyProfileData = {
        currentRole: {
          jobTitle: employeeContext.jobGroup || 'Unknown',
          department: employeeContext.department || 'Unknown',
          campus: employeeContext.campus || 'Unknown',
          lineManager: employeeContext.reportingManager || 'Unknown',
          employmentDate: new Date().toISOString() // TODO: Get from F&O data when available
        },
        qualifications: [],
        externalCareerHistory: [],
        professionalContext: {
          areasOfExpertise: [],
          languageProficiencies: [],
          geographicExperience: [],
          industryExperience: []
        },
        lastUpdated: new Date().toISOString(),
        completionStatus: 'incomplete' as const,
        nextUpdateDue: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
      }
      setEmployeeProfileData(emptyProfileData)
    } finally {
      setLoadingProfileData(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      {/* Tour Styles Injection */}
      <style>{tourStyles}</style>
      
      <div className="w-full mx-auto px-3 sm:px-4 md:px-6 lg:px-8 max-w-[98vw] 2xl:max-w-[1800px] min-w-0 overflow-x-clip">
        {/* Employee Performance Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-8 mb-8 bg-gradient-to-br from-heritage-blue via-heritage-blue/95 to-future-blue text-white relative overflow-hidden rounded-2xl shadow-2xl border border-white/20"
        >
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/5 pointer-events-none" />
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-future-blue/20 to-transparent rounded-full -translate-y-48 translate-x-48" />
          </div>
          
          <div className="relative px-3 sm:px-4 md:px-6 lg:px-8 pt-3 sm:pt-6 pb-2 sm:pb-4">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3 sm:mb-4 md:mb-6">
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="relative hidden xl:block flex-shrink-0"
                >
                  <img src="/assets/logos/Light-Logo-Impact-Growth2.png" alt="my Impact & Growth Journey" className="h-16 object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]" />
                </motion.div>
                <div className="h-12 w-px bg-gradient-to-b from-transparent via-white/30 to-transparent mx-1 hidden xl:block" />
                <motion.div
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <h1 className="font-primary text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-white tracking-tight">
                    my Impact & Growth Journey
                  </h1>
                </motion.div>
              </div>
              
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-wrap items-center gap-2 min-w-0 w-full sm:w-auto"
              >
                {/* Year & Period Selectors - grouped for tour highlight.
                    flex-wrap so the two selectors don't overflow the banner
                    alongside Explore/Profile at <=375px. */}
                <div className="flex flex-wrap items-center gap-2" data-tour="year-period-selectors">
                  {/* Global Year Selector - Always Visible */}
                  <Select
                    value={selectedYear.toString()}
                    onValueChange={(value) => setSelectedYear(parseInt(value))}
                  >
                    <SelectTrigger className="w-[105px] sm:w-[115px] bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm h-9 sm:h-10 text-xs sm:text-sm">
                      <Calendar className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                      <span>{selectedYear}</span>
                    </SelectTrigger>
                    <SelectContent>
                      {availableYears.map(year => (
                        <SelectItem key={year} value={year.toString()}>
                          <div className="flex items-center gap-2">
                            {year}
                            {year < SYSTEM_LAUNCH_YEAR && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Legacy</span>}
                            {year === currentYear && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Current</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Review Period Selector */}
                  <Select
                    value={selectedReviewPeriod}
                    onValueChange={(value: 'MIDYEAR' | 'YEAREND') => {
                      if (import.meta.env.DEV) console.log('🔄 Review Period Changed:', { from: selectedReviewPeriod, to: value })
                      userPickedPeriodRef.current = true
                      setSelectedReviewPeriod(value)
                    }}
                  >
                    <SelectTrigger className="w-[120px] sm:w-[130px] bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm h-9 sm:h-10 text-xs sm:text-sm">
                      {selectedReviewPeriod === 'MIDYEAR'
                        ? <CheckSquare className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                        : <Calendar className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                      }
                      <span>{selectedReviewPeriod === 'MIDYEAR' ? 'Mid-Year' : 'Year-End'}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MIDYEAR">Mid-Year</SelectItem>
                      <SelectItem value="YEAREND">Year-End</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Take Tour Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startTour}
                  className={HEADER_GHOST_BTN}
                  title="Explore the platform"
                >
                  {/* OLD: <HelpCircle /> Tour */}
                  <Sparkles className="h-3.5 w-3.5" />
                  <span className="ml-1.5 hidden lg:inline text-xs">Explore</span>
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenProfileManager}
                  className={HEADER_GHOST_BTN}
                  data-tour="profile-button"
                >
                  <User className="h-3.5 w-3.5" />
                  <span className="ml-1.5 hidden lg:inline text-xs">Profile</span>
                </Button>
              </motion.div>
            </div>

            {/* Employee Context Bar (left) + in-context guidance Watch button (bottom-right). */}
            <div className="flex items-end justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-3 md:gap-4 lg:gap-6 text-xs sm:text-sm text-white/90" data-tour="employee-header">
              <div className="flex items-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{employeeContext.jobGroup}</span>
              </div>
              <span className="hidden sm:inline">•</span>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{/* OLD: Reports to: */}People Leader: {employeeContext.reportingManager}</span>
              </div>
              <span className="hidden sm:inline">•</span>
              <div className="flex items-center gap-1.5 hidden sm:flex">
                <Calendar className="h-4 w-4" />
                <span>{employeeContext.performanceCycle}</span>
              </div>
              <span className="hidden md:inline">•</span>
              <span className="hidden md:inline">{employeeContext.campus}</span>
            </div>
            {guidanceSurface && (
              <VideoHelpButton
                surface={guidanceSurface}
                className="shrink-0 border-white/30 bg-white/15 text-white hover:bg-white/25"
              />
            )}
            </div>
          </div>
        </motion.header>

        {/* Staff apparel voucher waiting to be used — renders nothing otherwise
            (no voucher, already ordered, ordering not open, or any error). The
            margins sit it midway between the header and the tabs' -mt-6. */}
        <StaffOrderBanner className="-mt-4 mb-10" />

        {/* Performance Management Tabs */}
        <section className="mb-8 -mt-6">
          <Tabs value={activeTab} onValueChange={(value) => {
            setActiveTab(value as any)
            setSearchParams({ tab: value })
          }} className="w-full">
            <JourneyNavPublisher
              journey="employee"
              subtitles={{
                objectives: isLegacyYear ? `F&O ${selectedYear}` : `${objectiveStats.total} objectives`,
                portfolio: `${enhancedEvidence.length} ${enhancedEvidence.length === 1 ? 'entry' : 'entries'}`,
              }}
              hidden={hiddenSections}
              labels={isLegacyYear ? { objectives: 'Impact & Growth History' } : {}}
            />
            <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-md rounded-t-2xl min-w-0 max-w-full md:hidden">
              {/* Phone: dropdown selector (desktop navigates from the sidebar) */}
              <div className="md:hidden p-3">
                <Select value={activeTab} onValueChange={(value) => { setActiveTab(value as any); setSearchParams({ tab: value }) }}>
                  <SelectTrigger className="w-full h-11 bg-white border-gray-200 text-sm font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!outsideReviewCycle && (
                      <SelectItem value="objectives">{isLegacyYear ? 'Impact & Growth History' : 'my Objectives'}</SelectItem>
                    )}
                    {!isLegacyYear && (
                    <>
                      <SelectItem value="portfolio">my Impact Portfolio</SelectItem>
                      {/* OLD: Reviews & Sessions, Feedback, Development */}
                      {!outsideReviewCycle && <SelectItem value="planning">my Check-Ins</SelectItem>}
                      <SelectItem value="feedback">my "In-the-Moment" Feedback</SelectItem>
                      <SelectItem value="development">my Personalised Growth</SelectItem>
                    </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-b-2xl border border-t-0 md:rounded-2xl md:border-t border-white/50 shadow-xl px-3 pb-3 sm:px-6 sm:pb-6 md:px-8 md:pb-8 min-w-0 overflow-x-clip">
              {outsideReviewCycle && (
                <p role="note" className="mt-4 mb-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  You're not part of the performance review cycle, so objectives and check-ins don't show here.
                  Your Impact Portfolio, In-the-Moment Feedback and Personalised Growth are all still yours to use.
                </p>
              )}
              <TabsContent value="objectives" className="mt-0">
                <ObjectivesTab
                  user={user}
                  employeeContext={employeeContext}
                  selectedYear={selectedYear}
                  currentYear={currentYear}
                  selectedReviewPeriod={selectedReviewPeriod}
                  isLoading={isLoading}
                  error={error}
                  isLegacyYear={isLegacyYear}
                  hasObjectives={hasObjectives}
                  performancePlan={performancePlan}
                  kpaData={kpaData}
                  roleKPIs={roleKPIs}
                  evidence={enhancedEvidence}
                  filteredEvidence={filteredEvidence}
                  onAddEvidence={handleAddEvidenceEnhanced}
                  onUpdateEvidence={handleUpdateEvidence}
                  onDeleteEvidence={handleDeleteEvidence}
                  onUploadDocument={handleUploadDocument}
                  onUpdateEvidenceClassification={handleUpdateEvidenceClassification}
                  onRefreshEvidence={async () => {
                    if (user?.employeeId) {
                      await loadEvidence(user.employeeId, selectedYear)
                    }
                  }}
                  refetchPerformancePlan={refetchPerformancePlan}
                  refetchCollaborativeSessions={refetchCollaborativeSessions}
                  historicalData={historicalData}
                  isLoadingHistorical={isLoadingHistorical}
                  onOpenProfileManager={handleOpenProfileManager}
                  onViewKPIDetails={handleViewKPIDetails}
                  onCreateObjective={handleCreateObjective}
                  onUpdateObjective={handleUpdateObjective}
                  onDeleteObjective={handleDeleteObjective}
                  onViewObjectiveDetails={handleViewObjectiveDetails}
                />
              </TabsContent>

              {/* Portfolio Tab — top-level Impact Portfolio surface (private to employee) */}
              <TabsContent value="portfolio" className="mt-0">
                <PortfolioTab
                  user={user}
                  evidence={portfolioTabEvidence}
                  kpaData={kpaData}
                  onAddEvidence={handleAddEvidenceEnhanced}
                  onUpdateEvidence={handleUpdateEvidence}
                  onDeleteEvidence={handleDeleteEvidence}
                  onUploadDocument={handleUploadDocument}
                  onUpdateEvidenceClassification={handleUpdateEvidenceClassification}
                  onCarryForwardEvidence={async (evidenceId) => {
                    if (!user?.employeeId) return undefined
                    return await performanceService.carryForwardEvidence(user.employeeId, evidenceId)
                  }}
                  onReleaseEvidence={async (evidenceId) => {
                    if (!user?.employeeId) return undefined
                    const { evidence: updated } = await performanceService.releaseEvidence(
                      user.employeeId, evidenceId,
                    )
                    // Patch local state with the server's authoritative row so
                    // the toggle / lockState refresh immediately without a
                    // roundtrip re-fetch of the whole portfolio.
                    setEnhancedEvidence((prev) => prev.map((e) => e.id === evidenceId ? { ...e, ...updated } : e))
                    return updated as EnhancedEvidenceEntry
                  }}
                  onWithdrawEvidence={async (evidenceId) => {
                    if (!user?.employeeId) return undefined
                    const { evidence: updated } = await performanceService.withdrawEvidence(
                      user.employeeId, evidenceId,
                    )
                    setEnhancedEvidence((prev) => prev.map((e) => e.id === evidenceId ? { ...e, ...updated } : e))
                    return updated as EnhancedEvidenceEntry
                  }}
                  onRefreshEvidence={async () => {
                    if (user?.employeeId) {
                      await loadEvidence(user.employeeId, selectedYear)
                      await refetchPerformancePlan()
                    }
                  }}
                />
              </TabsContent>

              {/* Planning/Reviews Tab */}
              <TabsContent value="planning" className="mt-0">
                <ReviewsTab
                  employeeContext={employeeContext}
                  kpaData={kpaData}
                  evidence={enhancedEvidence}
                  selectedYear={selectedYear}
                  userId={effectiveUserId}
                  employeeId={user?.employeeId}
                  managerId={user?.managerId}
                  onLoadEvidence={loadEvidence}
                  onRefetchPerformancePlan={refetchPerformancePlan}
                  collaborativeSessions={collaborativeSessions}
                  isLoadingCollabSessions={loadingCollaborativeSessions}
                  onAddEvidence={handleAddEvidenceEnhanced}
                  onUpdateEvidence={handleUpdateEvidence}
                  onDeleteEvidence={handleDeleteEvidence}
                  onUploadDocument={handleUploadDocument}
                  onUpdateEvidenceClassification={handleUpdateEvidenceClassification}
                />
              </TabsContent>

              {/* Feedback Tab */}
              <TabsContent value="feedback" className="mt-0">
                <FeedbackTab
                  userRole={userRole}
                  userId={effectiveUserId}
                  employeeId={user?.employeeId}
                />
              </TabsContent>

              {/* Development Tab */}
              <TabsContent value="development" className="mt-0">
                <DevelopmentTab
                  userId={effectiveUserId}
                  numericUserId={user?.id ? parseInt(user.id) : undefined}
                  selectedYear={selectedYear}
                  userRole={userRole}
                  performancePlan={performancePlan}
                />
              </TabsContent>
            </div>
          </Tabs>
        </section>

        {/* Note: Objective loading/error states now handled inside ObjectivesTab */}
      </div>
      
      {/* Objective Workspace Modal */}
      {isWorkspaceOpen && selectedObjective && (
        <KPIWorkspace
          kpi={selectedObjective}
          onClose={handleCloseWorkspace}
          evidence={evidence}
          onAddEvidence={handleAddEvidence}
          aiInsights={aiInsights}
          isAILoading={isAILoading}
          onGenerateInsights={handleGenerateInsights}
          onAddMilestone={handleAddMilestone}
          onUpdateMilestone={handleUpdateMilestone}
          onDeleteMilestone={handleDeleteMilestone}
        />
      )}


      {/* Objective Setting Dialog */}
      <ObjectiveSettingDialog
        open={showObjectiveSourceDialog}
        onClose={() => setShowObjectiveSourceDialog(false)}
        kpas={kpaData}
        templates={[]}
        priorYearObjectives={[]}
        onCreateObjective={async (objective) => {
          try {
            if (import.meta.env.DEV) console.log('Creating objective:', objective)
            
            if (!user || !user.employeeId) {
              toast.error('User not authenticated. Please log in again.')
              return
            }

            // Transform objective data to match backend schema
            const objectiveData = {
              title: objective.title,
              description: objective.description,
              kpa_id: objective.kpaId,
              start_date: objective.startDate ? `${objective.startDate}T00:00:00Z` : undefined,
              end_date: objective.endDate ? `${objective.endDate}T23:59:59Z` : undefined,
              kpis: (objective.metrics || []).map((metric: string) => ({
                name: metric,
                description: '',
                target: '',
                unit: '',
                frequency: 'bi-annually',
                weight: 100
              }))
            }
            
            const created = await performanceService.createEmployeeObjective(
              user.employeeId,
              objectiveData
            )
            
            toast.success('Objective created successfully! Submitted for people leader approval.', { duration: 5000 })
            setShowObjectiveSourceDialog(false)
            
            // Create collaborative session for manager to review
            // This ensures a discussion is scheduled before approval
            if (import.meta.env.DEV) console.log('🔍 User object for collab session:', {
              employeeId: user.employeeId,
              displayName: user.displayName,
              managerId: user.managerId,
              managerEmployeeId: user.managerEmployeeId
            })
            
            if (user.managerId) {
              if (import.meta.env.DEV) console.log('✅ Manager ID found, creating collaborative session')
              try {
                await performanceService.createCollaborativeSession({
                  employee_id: user.employeeId,
                  employee_name: user.displayName || 'Team Member',
                  employee_role: user.jobTitle || '',
                  manager_id: user.managerEmployeeId || user.managerId,
                  manager_name: 'People Leader', // People leader name not always available
                  session_type: 'objective_revision',
                  performance_year: new Date().getFullYear(),
                  scheduled_by: 'system'
                })
                if (import.meta.env.DEV) console.log('Collaborative session created for objective review')
                // Refresh sessions list to show new session
                await refetchCollaborativeSessions()
              } catch (sessionError) {
                // Session might already exist, which is fine
                if (import.meta.env.DEV) console.log('Note: Collaborative session may already exist', sessionError)
                // Still refresh to show existing session
                await refetchCollaborativeSessions()
              }
            } else {
              console.warn('⚠️ No manager ID found for user, cannot create collaborative session. User:', user)
            }
            
            // Reload objectives
            await refetchPerformancePlan()
          } catch (error) {
            console.error('Failed to create objective:', error)
            toast.error('Failed to create objective. Please try again.')
          }
        }}
      />

      {/* Profile Manager Dialog */}
      {showProfileManager && (
        <Dialog open={showProfileManager} onOpenChange={setShowProfileManager}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Professional Profile</DialogTitle>
            </DialogHeader>
            {loadingProfileData ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-heritage-blue mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading your profile...</p>
                </div>
              </div>
            ) : employeeProfileData ? (
              <AnnualProfileManager
                profileData={employeeProfileData}
                userRole={userRole}
                updateTrigger="annual"
                internalCareerHistory={internalCareerHistory}
                onSaveProfile={async (data) => {
                  if (import.meta.env.DEV) console.log('Save profile:', data)
                  try {
                    // Transform back to API format and save
                    await profileService.saveCompleteProfile({
                      qualifications: data.qualifications.map(q => ({
                        qualification_type: q.type,
                        institution: q.institution || '',
                        country: q.country || 'south_africa',
                        qualification_name: q.qualificationName ?? undefined,
                        cesm_category: q.cesmCategory ?? undefined,
                        field_of_study: q.fieldOfStudy ?? undefined,
                        nqf_level: q.nqfLevel ?? undefined,
                        year_completed: q.yearCompleted ?? undefined,
                        in_progress: q.inProgress || false,
                        expected_completion_year: q.expectedCompletionYear ?? undefined,
                        international_equivalency: q.internationalEquivalency ?? undefined,
                        saqa_evaluated: q.saqaEvaluated || false,
                        saqa_reference_number: q.saqaReferenceNumber ?? undefined,
                        professional_body: q.professionalBody ?? undefined,
                        membership_number: q.membershipNumber ?? undefined,
                        membership_status: q.membershipStatus ?? undefined,
                        thesis: q.thesis || false,
                        thesis_title: q.thesisTitle ?? undefined,
                        verification_document_url: q.verificationDocument ?? undefined
                      })),
                      career_history: data.externalCareerHistory.map(c => ({
                        organization: c.organization,
                        position: c.position,
                        start_date: c.startDate,
                        end_date: c.endDate || undefined,
                        direct_reports: c.directReports || 0,
                        key_achievements: c.keyAchievements.split(', ').filter(Boolean),
                        skills_developed: c.skillsDeveloped
                      })),
                      professional_context: {
                        areas_of_expertise: data.professionalContext.areasOfExpertise,
                        language_proficiencies: data.professionalContext.languageProficiencies.map(l => ({
                          language: l.language,
                          level: l.proficiency
                        })),
                        geographic_experience: data.professionalContext.geographicExperience,
                        industry_experience: data.professionalContext.industryExperience,
                        completion_status: data.completionStatus === 'complete' ? 'complete' : 'incomplete'
                      }
                    })
                    toast.success('Profile saved successfully')
                  } catch (error) {
                    console.error('Failed to save profile:', error)
                    toast.error('Failed to save profile')
                  }
                }}
                onCompleteProfile={async (data) => {
                  if (import.meta.env.DEV) console.log('Complete profile:', data)
                  try {
                    await profileService.saveCompleteProfile({
                      qualifications: data.qualifications.map(q => ({
                        qualification_type: q.type,
                        institution: q.institution || '',
                        country: q.country || 'south_africa',
                        qualification_name: q.qualificationName ?? undefined,
                        cesm_category: q.cesmCategory ?? undefined,
                        field_of_study: q.fieldOfStudy ?? undefined,
                        nqf_level: q.nqfLevel ?? undefined,
                        year_completed: q.yearCompleted ?? undefined,
                        in_progress: q.inProgress || false,
                        expected_completion_year: q.expectedCompletionYear ?? undefined,
                        international_equivalency: q.internationalEquivalency ?? undefined,
                        saqa_evaluated: q.saqaEvaluated || false,
                        saqa_reference_number: q.saqaReferenceNumber ?? undefined,
                        professional_body: q.professionalBody ?? undefined,
                        membership_number: q.membershipNumber ?? undefined,
                        membership_status: q.membershipStatus ?? undefined,
                        thesis: q.thesis || false,
                        thesis_title: q.thesisTitle ?? undefined,
                        verification_document_url: q.verificationDocument ?? undefined
                      })),
                      career_history: data.externalCareerHistory.map(c => ({
                        organization: c.organization,
                        position: c.position,
                        start_date: c.startDate,
                        end_date: c.endDate || undefined,
                        direct_reports: c.directReports || 0,
                        key_achievements: c.keyAchievements.split(', ').filter(Boolean),
                        skills_developed: c.skillsDeveloped
                      })),
                      professional_context: {
                        areas_of_expertise: data.professionalContext.areasOfExpertise,
                        language_proficiencies: data.professionalContext.languageProficiencies.map(l => ({
                          language: l.language,
                          level: l.proficiency
                        })),
                        geographic_experience: data.professionalContext.geographicExperience,
                        industry_experience: data.professionalContext.industryExperience,
                        completion_status: 'complete'
                      }
                    })
                    toast.success('Profile completed successfully')
                    setShowProfileManager(false)
                  } catch (error) {
                    console.error('Failed to complete profile:', error)
                    toast.error('Failed to complete profile')
                  }
                }}
              />
            ) : (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">Unable to load profile data. Please try again.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Note: Review Preparation Workspace moved to ReviewsTab component */}

      {/* Objective Creation Wizard Dialog */}
      {showObjectiveWizard && (
        <Dialog open={showObjectiveWizard} onOpenChange={setShowObjectiveWizard}>
          <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto p-0" aria-describedby="wizard-description">
            <span id="wizard-description" className="sr-only">
              Create a new objective with SMART criteria and KPIs through a guided 4-step process
            </span>
            <ImprovedObjectiveWizard
              onSave={async (objectives) => {
                // Ensure user is authenticated
                if (!user || !user.employeeId) {
                  toast.error('User not authenticated. Please log in again.')
                  return
                }

                try {
                  // Create each objective with validation already done in wizard
                  const createdObjectives = []
                  
                  for (const objective of objectives) {
                    const objectiveData = {
                      title: objective.title,
                      description: objective.description,
                      kpa_id: objective.kpa || objective.kpaName,
                      start_date: objective.startDate ? `${objective.startDate}T00:00:00Z` : undefined,
                      end_date: objective.endDate ? `${objective.endDate}T23:59:59Z` : undefined,
                      kpis: (objective.kpis || []).map((kpi: any) => ({
                        name: kpi.title || kpi.name,
                        description: kpi.description || '',
                        target: kpi.targetValue || kpi.target || '',
                        unit: kpi.unit || '',
                        frequency: kpi.frequency || 'bi-annually',
                        weight: kpi.weight || 100
                      }))
                    }
                    
                    const created = await performanceService.createEmployeeObjective(
                      user.employeeId,
                      objectiveData
                    )
                    createdObjectives.push(created)
                    
                    if (import.meta.env.DEV) console.log(`Created objective: ${created.objective.title} with ${created.kpis_created} KPIs (Status: ${created.status})`)
                  }
                  
                  // Create collaborative session for manager to review new objectives
                  if (user?.managerId) {
                    try {
                      await performanceService.createCollaborativeSession({
                        employee_id: user.employeeId,
                        employee_name: user.displayName || 'Team Member',
                        employee_role: user.jobTitle || '',
                        manager_id: user.managerEmployeeId || user.managerId,
                        manager_name: 'People Leader',
                        session_type: 'objective_revision',
                        performance_year: new Date().getFullYear(),
                        scheduled_by: 'system'
                      })
                      if (import.meta.env.DEV) console.log('Collaborative session created for wizard objectives')
                      await refetchCollaborativeSessions()
                    } catch (sessionError) {
                      if (import.meta.env.DEV) console.log('Note: Collaborative session may already exist', sessionError)
                      await refetchCollaborativeSessions()
                    }
                  }
                  
                  toast.success(
                    `Created ${createdObjectives.length} objective${createdObjectives.length > 1 ? 's' : ''} successfully! ` +
                    `Submitted for people leader approval.`,
                    { duration: 5000 }
                  )
                  
                  setShowObjectiveWizard(false)
                  
                  // Refresh data instead of full page reload
                  await refetchPerformancePlan()
                } catch (error) {
                  console.error('Failed to create objectives:', error)
                  toast.error('Failed to create objectives. Please try again.')
                }
              }}
              onCancel={() => setShowObjectiveWizard(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Note: View Completed Review Dialog and loading overlay moved to ReviewsTab component */}

      {/* Note: FeedbackRequestDialog moved to FeedbackTab component */}
    </div>
  )
}
